import { ClipOverlay, OverlayType } from '../../../reactvideoeditor/pro/types';
import { MAIN_TRACK_ID } from '../../../constants';
import { getOverlayUniqueId } from '../../../utils/editor/editorUtils';

export interface ProgramCut {
    start: number;
    end: number;
}

export interface ProgramItem {
    src: string;
    cuts: ProgramCut[];
    type?: string;
}

export interface ItemRange {
    /** Index into the original `program` array. */
    itemIdx: number;
    /** First frame on the synthetic timeline (inclusive). */
    fromFrame: number;
    /** Frame just after the last frame of this item (exclusive). */
    toFrame: number;
}

/**
 * Convert a card `program` (sequence of HLS sources, each with cut ranges) to
 * the `ClipOverlay[]` the editor engine consumes.
 *
 * One overlay per cut. Overlays are laid out back-to-back on a synthetic
 * timeline at `timelineFps`. `videoStartTime` carries the cut's offset within
 * the source, so the engine's existing extractor pipeline plays the correct
 * range without changes.
 *
 * Note: separate cuts on the same `src` produce separate `HLSFrameExtractor`
 * instances inside the engine (one per overlay id). They share manifest /
 * segment metadata via `hlsSharedData`, so the cost is bounded — only the
 * currently-active extractor decodes; upcoming ones are warmed via the
 * engine's existing `SOURCE_WARMUP_SECONDS` window.
 */
export function programToOverlays(
    cardId: string,
    program: ProgramItem[],
    timelineFps: number,
): { overlays: ClipOverlay[]; itemRanges: ItemRange[]; totalFrames: number } {
    const overlays: ClipOverlay[] = [];
    const itemRanges: ItemRange[] = [];
    let cursor = 0;

    for (let itemIdx = 0; itemIdx < program.length; itemIdx++) {
        const item = program[itemIdx];
        if (!item?.src || !Array.isArray(item.cuts)) continue;
        const itemStart = cursor;

        for (let cutIdx = 0; cutIdx < item.cuts.length; cutIdx++) {
            const cut = item.cuts[cutIdx];
            if (!cut || !Number.isFinite(cut.start) || !Number.isFinite(cut.end)) continue;
            const durSec = cut.end - cut.start;
            if (!(durSec > 0)) continue;
            const durFrames = Math.max(1, Math.round(durSec * timelineFps));

            overlays.push({
                id: getOverlayUniqueId(`${cardId}:${itemIdx}:${cutIdx}:${item.src}:${cut.start}:${cut.end}`),
                groupId: undefined,
                type: OverlayType.VIDEO,
                src: item.src,
                videoStartTime: cut.start,
                from: cursor,
                durationInFrames: durFrames,
                row: MAIN_TRACK_ID,
                rotation: 0,
                left: 0,
                top: 0,
                width: 1280,
                height: 720,
                isDragging: false,
                content: '',
                styles: { opacity: 1 } as any,
            } as ClipOverlay);

            cursor += durFrames;
        }

        if (cursor > itemStart) {
            itemRanges.push({ itemIdx, fromFrame: itemStart, toFrame: cursor });
        }
    }

    return { overlays, itemRanges, totalFrames: cursor };
}

/** Locate which program item contains a given frame on the synthetic timeline. */
export function frameToItemIndex(itemRanges: ItemRange[], frame: number): number {
    for (const r of itemRanges) {
        if (frame >= r.fromFrame && frame < r.toFrame) return r.itemIdx;
    }
    return itemRanges.length > 0 ? itemRanges[0].itemIdx : 0;
}
